/**
 * Theme management (items #8, #9, #14 Phase 1).
 *
 * Theme preference: 'light' | 'dark' | 'earth' | 'system' (default 'light').
 * - Stored in localStorage under key 'poe-theme'.
 * - System preference detected via matchMedia('(prefers-color-scheme: dark)').
 * - FOUC prevention happens in index.html inline script before React mounts.
 * - This module syncs React state with localStorage and listens for system
 *   preference changes when 'system' is active.
 */
import { useEffect, useState, useCallback } from 'react';

export type ThemePreference = 'light' | 'dark' | 'earth' | 'system';
export type ResolvedTheme = 'light' | 'dark' | 'earth';

const STORAGE_KEY = 'poe-theme';
const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#ffffff',
  dark: '#09090b',
  earth: '#faf7ef',
};

function readStored(): ThemePreference {
  // Default 'light' per Rick's directive. Users can choose light, dark, earth,
  // or system from the nav rail toggle.
  if (typeof window === 'undefined') return 'light';
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'earth' || v === 'system') return v;
  } catch {
    /* ignore */
  }
  return 'light';
}

function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return pref;
}

function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.classList.toggle('earth', resolved === 'earth');
  root.dataset.theme = resolved;

  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = THEME_COLOR[resolved];

  // Keep the status-bar-style meta consistent with the resolved theme (dark ->
  // translucent so the dark surface paints behind the bar; light/earth -> opaque
  // so the icons stay legible). iOS only reads this at standalone launch (the
  // boot script sets it from the stored theme), so a runtime switch takes effect
  // on the next PWA launch; we still update it here to keep the DOM consistent.
  const statusBar = document.querySelector<HTMLMetaElement>(
    'meta[name="apple-mobile-web-app-status-bar-style"]',
  );
  if (statusBar) statusBar.content = resolved === 'dark' ? 'black-translucent' : 'default';
}

/**
 * Paint a theme handed down by an embedding shell, WITHOUT storing it.
 *
 * Rick, 2026-08-17: POE rendering light inside a dark LA-AI is jarring. When
 * POE is embedded, the shell's theme wins for the life of that frame.
 *
 * Deliberately does not touch localStorage. The shell's choice is a property
 * of the frame, not of this user's POE preference - persisting it would mean
 * opening POE standalone tomorrow silently inherits whatever LA-AI happened to
 * be set to, overwriting a choice the user made here.
 *
 * 'earth' is reachable only from POE's own toggle; a shell can only ever ask
 * for light or dark, which is all LA-AI has.
 */
export function applyShellTheme(resolved: ResolvedTheme): void {
  applyTheme(resolved);
}

/** Narrow an untrusted postMessage value to a theme this app can paint. */
export function asResolvedTheme(value: unknown): ResolvedTheme | null {
  return value === 'light' || value === 'dark' || value === 'earth' ? value : null;
}

/** Read the resolved theme right now (sync; safe to call anytime after mount). */
export function getResolvedTheme(): ResolvedTheme {
  return resolveTheme(readStored());
}

/**
 * useTheme - tri-state theme hook.
 *
 * @returns { preference, resolved, setPreference }
 *   preference: 'light' | 'dark' | 'earth' | 'system' (user's chosen mode)
 *   resolved:   'light' | 'dark' | 'earth' (what is actually rendered now)
 *   setPreference: setter that writes localStorage + applies + dispatches event
 */
export function useTheme() {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStored);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readStored()));

  // Listen for OS-level theme changes when preference is 'system'
  useEffect(() => {
    if (preference !== 'system' || typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const newResolved: ResolvedTheme = mq.matches ? 'dark' : 'light';
      setResolved(newResolved);
      applyTheme(newResolved);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    setPreferenceState(next);
    const newResolved = resolveTheme(next);
    setResolved(newResolved);
    applyTheme(newResolved);
  }, []);

  return { preference, resolved, setPreference };
}
